import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
	IAuthProvider,
	IPlugin,
	IUser,
	ModelService,
} from '../models';
import {
	PluginInjectorApplicationPlugin,
	IMenuItem,
	IModuleWidget,
	PluginInjectorService,
} from '../plugin-injector';
import { IOpenIDClaims } from './auth.service';

export type AuthSessionStatus = 'existing' | 'created' | 'disabled' | 'error';

export interface IAuthSessionResult {
	status: AuthSessionStatus;
	user?: IUser;
	errorMessage?: string;
}

/**
 * AuthCallback — Process flow before forwarding to the dashboard runs server-side in AuthOrchestrationService.
 *
 *  1. POST /api/auth/session/login
 *     Backend: user lookup + retry, new-user creation, dashboard assignment.
 *     Sets externProviderAuthenticated + userEnabled for AuthCallbackComponent.
 *
 *  2. POST /api/auth/session/register-plugins
 *     Backend: plugin upsert, permission seeding, AppPlugin seeding.
 */
@Injectable({
	providedIn: 'root',
})
export default abstract class AuthCallback {
	externOAuth2Provider!: IAuthProvider;

	/**
	 * True when POST /api/auth/session/login returns 'existing' or 'created'.
	 * AuthCallbackComponent uses this to distinguish a backend outage (false)
	 * from a disabled account (true + userEnabled=false).
	 */
	externProviderAuthenticated: boolean = false;

	/**
	 * True when the backend confirms the user exists and is enabled.
	 * AuthCallbackComponent navigates to /dashboard when true, shows
	 * ACCOUNT_DISABLED error when false.
	 */
	userEnabled: boolean = false;

	protected readonly modelService = inject(ModelService);
	protected readonly pluginInjectorService = inject(PluginInjectorService);
	protected readonly http = inject(HttpClient);

	/**
	 * Called by AuthCallbackComponent after OIDC login succeeds.
	 */
	async handleAuthenticationSuccess(authUser: { info: IOpenIDClaims }): Promise<void> {
		if (!authUser) {
			console.error('[AuthCallback] No auth user provided');
			return;
		}

		let result: IAuthSessionResult;

		try {
			result = await firstValueFrom(
				this.http.post<IAuthSessionResult>('/api/auth/session/login', {
					claims: authUser.info,
				})
			);
		} catch (error) {
			// Network or BFF failure — both flags stay false.
			// AuthCallbackComponent reads isProviderAvailable() to pick the error.
			console.error('[AuthCallback] POST /api/auth/session/login failed:', error);
			return;
		}

		switch (result.status) {
			case 'existing':
			case 'created':
				this.externProviderAuthenticated = true;
				this.userEnabled = true;
				if (result.user) {
					this.modelService._user.setUser(result.user);
				}
				break;

			case 'disabled':
				this.externProviderAuthenticated = true;
				this.userEnabled = false;
				return; // No plugin sync needed for disabled users

			case 'error':
			default:
				this.externProviderAuthenticated = false;
				this.userEnabled = false;
				console.error('[AuthCallback] Backend login error:', result.errorMessage);
				return;
		}

		await this.syncRegisteredPlugins();
	}

	handleAuthenticationFailure(error: any): void {
		console.error('Authentication Failed:', error);
	}

	/**
	 * Sends serialisable plugin descriptors to POST /api/auth/session/register-plugins.
	 * Backend does all DB work and returns IPlugin[] with _id, installed_at etc.
	 */
	private async syncRegisteredPlugins(): Promise<void> {
		const plugins = this.pluginInjectorService.getPlugins();

		if (plugins.length === 0) {
			return;
		}

		const descriptors = plugins.map(p => ({
			name:        p.name,
			enabled:     p.enabled,
			description: p.description,
			version:     p.version,
			settings:    p.settings,
			permissions: p.permissions,
		}));

		let results: IPlugin[];

		try {
			results = await firstValueFrom(
				this.http.post<IPlugin[]>(
					'/api/auth/session/register-plugins',
					{ plugins: descriptors }
				)
			);
		} catch (error) {
			// Fatal — without _id / pluginId the plugin architecture cannot function.
			// Treat the same as a login failure: clear flags so AuthCallbackComponent
			// shows an error instead of navigating to /dashboard.
			console.error('[AuthCallback] POST /api/auth/session/register-plugins failed:', error);
			this.externProviderAuthenticated = false;
			this.userEnabled = false;
			return;
		}

		for (const result of results) {
			const plugin = plugins.find(
				(p: PluginInjectorApplicationPlugin.IApplicationPlugin) => p.name === result.name
			);
			if (!plugin) continue;
			this.updateRegisterPluginInfos(plugin, result);
		}
	}

	/**
	 * Writes backend-resolved IPlugin data back into application.
	 * Stamps pluginId on all widgets and menuItems.
	 */
	private updateRegisterPluginInfos(
		registerPlugin: PluginInjectorApplicationPlugin.IApplicationPlugin,
		plugin: IPlugin
	): void {
		registerPlugin._id          = plugin._id;
		registerPlugin.name         = plugin.name;
		registerPlugin.enabled      = plugin.enabled;
		registerPlugin.description  = plugin.description;
		registerPlugin.version      = plugin.version;
		registerPlugin.installed_at = plugin.installed_at;

		if (plugin.settings)    registerPlugin.settings    = plugin.settings;
		if (plugin.permissions) registerPlugin.permissions = plugin.permissions;

		if (registerPlugin.modules) {
			registerPlugin.modules.forEach((pluginModule: PluginInjectorApplicationPlugin.IPluginModule) => {
				if (pluginModule.widgets) {
					pluginModule.widgets.forEach((widget: IModuleWidget) => {
						widget.pluginId = plugin._id;
					});
				}
			});
		}

		if (registerPlugin.menuItems) {
			registerPlugin.menuItems.forEach((item: IMenuItem) => {
				item.pluginId = plugin._id;
			});
		} else {
			registerPlugin.menuItems = [];
		}
	}

}
